import { ref, reactive, computed, watch } from 'vue'
import type { Specialite, SpecialiteFormData } from '~/types/administration'
import type { ApiResponse } from '~/types/api'
import { useApi } from './useApi'
import { toast } from 'vue3-toastify'

interface SpecialiteState {
    specialites: Specialite[]
    specialite: Specialite | null
    loading: boolean
    errors: Record<string, string[]>
    filters: {
        search: string
        status: 'Tous' | 'Actifs' | 'Inactifs'
        page: number
        per_page: number
    }
    pagination: {
        total: number
        current_page: number
        per_page: number
    }
}

export function useSpecialite() {
    const { apiData } = useApi()

    const state = reactive<SpecialiteState>({
        specialites: [],
        specialite: null,
        loading: false,
        errors: {},
        filters: {
            search: '',
            status: 'Tous',
            page: 1,
            per_page: 50
        },
        pagination: {
            total: 0,
            current_page: 1,
            per_page: 50
        }
    })

    const formRef = ref()

    const headers = [
        { title: 'Code', align: 'start' as const, key: 'code', sortable: true },
        { title: 'Intitulé', align: 'start' as const, key: 'intitule', sortable: true },
        { title: 'Statut', align: 'start' as const, key: 'is_active', sortable: true },
        { title: 'Actions', align: 'center' as const, key: 'actions', sortable: false }
    ]

    const rules = {
        intitule: [
            (v: string) => !!v || "L'intitulé est requis",
            (v: string) => v.length <= 255 || "L'intitulé ne doit pas dépasser 255 caractères"
        ],
        code: [
            (v: string) => !v || v.length <= 20 || 'Le code ne doit pas dépasser 20 caractères'
        ],
        required: (v: string) => !!v || 'Ce champ est requis',
        maxLength: (v: string) => v.length <= 255 || 'Le texte ne doit pas dépasser 255 caractères'
    }

    const loadingState = ref(false)
    let searchTimeout: NodeJS.Timeout | null = null

    const filteredSpecialites = computed(() => {
        return state.specialites.filter(specialite => {
            const searchTerm = state.filters.search.toLowerCase().trim();
            
            // Log de débogage pour vérifier la normalisation
            if (searchTerm) {
            }
            
            const matchSearch = !searchTerm || 
                specialite.intitule?.toLowerCase().includes(searchTerm) ||
                (specialite.code && specialite.code.toLowerCase().includes(searchTerm));
            const matchStatus = state.filters.status === 'Tous' || 
                (state.filters.status === 'Actifs' && specialite.is_active) ||
                (state.filters.status === 'Inactifs' && !specialite.is_active);
            return matchSearch && matchStatus;
        });
    });

    async function fetchSpecialites() {
        try {
            loadingState.value = true;
            const params = new URLSearchParams();
            // Ne pas envoyer 'search' au backend; le filtrage est effectué côté client pour insensibilité à la casse
            if (state.filters.status !== 'Tous') {
                params.append('is_active', (state.filters.status === 'Actifs').toString());
            }
            // Charger tout pour pagination client
            params.append('per_page', 'all');
            const response = await apiData<ApiResponse<Specialite[]>>(`/specialites?${params.toString()}`);
            if (response?.success) {
                state.specialites = response.data;
                state.pagination = {
                    total: response.data.length,
                    current_page: 1,
                    per_page: state.filters.per_page
                };
            }
        } catch (error) {
            //console.error('Erreur lors du chargement des spécialités:', error);
            state.specialites = [];
        } finally {
            loadingState.value = false;
        }
    }

    // Watchers ciblés
    watch(() => state.filters.status, () => fetchSpecialites())
    watch(() => state.filters.search, () => {
        if (searchTimeout) clearTimeout(searchTimeout)
        searchTimeout = setTimeout(() => fetchSpecialites(), 300)
    })

    async function fetchSpecialite(id: number) {
        try {
            state.loading = true
            const response = await apiData<ApiResponse<Specialite>>(`/specialites/${id}`)
            if (response.success) {
                state.specialite = response.data
                return response.data
            } else {
                toast.error(response.message || 'Erreur lors de la récupération de la spécialité')
                return null
            }
        } catch (error) {
            toast.error("Erreur lors de la récupération de la spécialité")
            return null
        } finally {
            state.loading = false
        }
    }

    async function deleteSpecialite(id: number) {
        try {
            const response = await apiData<ApiResponse<null>>(`/specialites/${id}`, {
                method: 'DELETE'
            })
            if (response.success) {
                await fetchSpecialites()
                return { success: true, message: 'Spécialité supprimée avec succès' }
            }
            if (response.message && response.message.includes('maquette active existe')) {
                toast.error(response.message)
                await fetchSpecialites()
                return { success: false, message: response.message, isMaquetteActiveError: true }
            }
            return { 
                success: false, 
                message: response.message || "Une erreur s'est produite lors de la suppression" 
            }
        } catch (error: any) {
            const errorMessage = error?.response?._data?.message || error?.data?.message || "Une erreur s'est produite"
            if (errorMessage.includes('maquette active existe')) {
                toast.error(errorMessage)
                await fetchSpecialites()
                return { success: false, message: errorMessage, isMaquetteActiveError: true }
            }
            return { success: false, message: errorMessage }
        }
    }

    async function createSpecialite(data: SpecialiteFormData) {
        try {
            const response = await apiData<ApiResponse<Specialite>>('/specialites', {
                method: 'POST',
                body: JSON.stringify(data)
            })
            if (response.success) {
                await fetchSpecialites()
                return { success: true, message: 'Spécialité créée avec succès' }
            }
            return { 
                success: false, 
                message: response.message || "Une erreur s'est produite lors de la création" 
            }
        } catch (error: any) {
            const errorMessage = error?.response?._data?.message || error?.data?.message || "Une erreur s'est produite"
            if (error?.response?._data?.data) {
                const validationErrors = error.response._data.data;
                return { 
                    success: false, 
                    message: errorMessage,
                    errors: validationErrors
                }
            }
            return { success: false, message: errorMessage }
        }
    }

    async function updateSpecialite(id: number, data: SpecialiteFormData) {
        try {
            const response = await apiData<ApiResponse<Specialite>>(`/specialites/${id}`, {
                method: 'PUT',
                body: JSON.stringify(data)
            })
            if (response.success) {
                await fetchSpecialites()
                return { success: true, message: 'Spécialité mise à jour avec succès' }
            }
            return { 
                success: false, 
                message: response.message || "Une erreur s'est produite lors de la mise à jour" 
            }
        } catch (error: any) {
            const errorMessage = error?.response?._data?.message || error?.data?.message || "Une erreur s'est produite"
            if (error?.response?._data?.data) {
                const validationErrors = error.response._data.data;
                return { 
                    success: false, 
                    message: errorMessage,
                    errors: validationErrors
                }
            }
            return { success: false, message: errorMessage }
        }
    }

    async function toggleActive(id: number) {
        try {
            const response = await apiData<ApiResponse<Specialite>>(`/specialites/${id}/toggle-active`, {
                method: 'PATCH'
            })
            if (response.success) {
                await fetchSpecialites()
                return { success: true, message: 'État de la spécialité modifié avec succès' }
            }
            return { 
                success: false, 
                message: response.message || "Une erreur s'est produite lors de la modification de l'état" 
            }
        } catch (error: any) {
            const errorMessage = error?.response?._data?.message || error?.data?.message || "Une erreur s'est produite"
            return { success: false, message: errorMessage }
        }
    }

    function resetForm() {
        if (formRef.value) {
            formRef.value.reset()
        }
        state.errors = {}
    }

    // Génération automatique du code à partir de l'intitulé
    const generateCodeFromIntitule = (intitule: string): string => {
        if (!intitule.trim()) return '';
        // Nettoyer l'intitulé : supprimer les accents et convertir en majuscules
        const cleanIntitule = removeAccents(intitule.trim().toUpperCase());
        // Prendre la première lettre de chaque mot (max 6 lettres)
        const words = cleanIntitule.split(/\s+/);
        let code = '';
        words.forEach(word => {
            code += word.charAt(0);
        });
        return code.substring(0, 6);
    };

    // Fonction pour supprimer les accents
    const removeAccents = (str: string): string => {
        const search = ['à', 'á', 'â', 'ã', 'ä', 'å', 'æ', 'ç', 'è', 'é', 'ê', 'ë', 'ì', 'í', 'î', 'ï', 'ð', 'ñ', 'ò', 'ó', 'ô', 'õ', 'ö', 'ø', 'ù', 'ú', 'û', 'ü', 'ý', 'ÿ', 'À', 'Á', 'Â', 'Ã', 'Ä', 'Å', 'Æ', 'Ç', 'È', 'É', 'Ê', 'Ë', 'Ì', 'Í', 'Î', 'Ï', 'Ð', 'Ñ', 'Ò', 'Ó', 'Ô', 'Õ', 'Ö', 'Ø', 'Ù', 'Ú', 'Û', 'Ü', 'Ý'];
        const replace = ['a', 'a', 'a', 'a', 'a', 'a', 'ae', 'c', 'e', 'e', 'e', 'e', 'i', 'i', 'i', 'i', 'd', 'n', 'o', 'o', 'o', 'o', 'o', 'o', 'u', 'u', 'u', 'u', 'y', 'y', 'A', 'A', 'A', 'A', 'A', 'A', 'AE', 'C', 'E', 'E', 'E', 'E', 'I', 'I', 'I', 'I', 'D', 'N', 'O', 'O', 'O', 'O', 'O', 'O', 'U', 'U', 'U', 'U', 'Y'];
        return str.split('').map(char => {
            const index = search.indexOf(char);
            return index !== -1 ? replace[index] : char;
        }).join('');
    };

    // Pour la preview du code auto-généré
    const updateAutoGeneratedCode = (specialiteData: SpecialiteFormData) => {
        if (specialiteData.code_auto_generated) {
            return generateCodeFromIntitule(specialiteData.intitule);
        }
        return '';
    };

    return {
        specialites: filteredSpecialites,
        specialite: computed(() => state.specialite),
        loading: loadingState,
        errors: state.errors,
        filters: state.filters,
        pagination: state.pagination,
        formRef,
        headers,
        rules,
        fetchSpecialites,
        fetchSpecialite,
        deleteSpecialite,
        createSpecialite,
        updateSpecialite,
        toggleActive,
        resetForm,
        generateCodeFromIntitule,
        updateAutoGeneratedCode
    }
} 